import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { Button, Loader, Switch, Typography } from '@inplayer-org/inplayer-ui';
import { FiPlus } from 'react-icons/fi';

// actions
import {
  createDonationOption,
  deleteDonation,
  editDonationOption,
  fetchDonations,
  updateCustomDonation,
} from 'ducks/merchantDucks/items/actions';
import { openModal } from 'ducks/merchantDucks/modal/actions';

// selectors
import { getIsDonationCustomPriceEnabled, getIsDonationLoading, getItemId } from 'ducks/merchantDucks/items/selectors';
import { getFeatures } from 'ducks/merchantDucks/user/selectors';

// types
import { DonationFee } from 'ducks/merchantDucks/items/types';
import { ModalNames } from 'ducks/merchantDucks/modal/types';
import { FeatureNames } from 'ducks/merchantDucks/user/types';

// utils
import { Optional } from 'utils/types';
import { isDevelopmentEnv } from 'utils/helpers';

// components
import { ContentContainer, FlexContainer } from 'components/SharedStyledElements';
import { LoaderContainer } from 'pages/MerchantPages/Assets/Packages/ManageAssets/DroppableColumn';
import DonationsTable from 'pages/MerchantPages/Assets/components/Donations/DonationsTable';
import CreateDonationForm, {
  FormValues as DonationFormValues,
} from 'pages/MerchantPages/Assets/components/Donations/CreateDonationForm';
import AttentionMessage from 'components/AttentionMessage';

const Wrapper = styled.div`
  margin: 2rem 0;
`;
export const Container = styled.div`
  padding: 2rem 6%;
`;

const StyledFlexContainer = styled(FlexContainer)`
  margin-bottom: 1.25rem;
`;

interface Props {
  closePanel: () => void;
}

const Donations: React.FC<Props> = ({ closePanel }) => {
  const dispatch = useDispatch();

  const [isNewDonationScreen, setIsNewDonationScreen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [donationInEdit, setDonationInEdit] = useState<Optional<DonationFormValues>>(null);

  const itemId = useSelector(getItemId);
  const isEnabledCustomOption = useSelector(getIsDonationCustomPriceEnabled);
  const isLoading = useSelector(getIsDonationLoading);
  const features = useSelector(getFeatures);

  const isDonationFeatureEnabled = !!features.find(
    (feature) => feature.name === FeatureNames.DONATIONS && feature.enabled
  );

  useEffect(() => {
    dispatch(fetchDonations(itemId));
  }, [itemId, isEnabledCustomOption]);

  const handleEnabledCustomOption = (value: boolean) => {
    dispatch(updateCustomDonation(itemId, value));
  };

  const handleNewDonation = (value: boolean) => () => {
    setIsNewDonationScreen(value);
    setIsEditing(false);
  };
  const deleteDonationPrice = (id: number) => () => {
    dispatch(deleteDonation(itemId, id));
  };

  const handleDeleteModal = () => {
    dispatch(openModal(ModalNames.DeletePriceDonation));
  };

  const handleSave = async (formValues: DonationFormValues) => {
    const { amount, currency, description } = formValues;

    if (isEditing && donationInEdit) {
      dispatch(editDonationOption(itemId, donationInEdit.id!, Number(amount), currency, description));
    } else {
      dispatch(createDonationOption(itemId, Number(amount), currency, description));
    }

    closePanel();
  };
  const handleEditDonation = (donationFee: DonationFee) => {
    if (isDonationFeatureEnabled) {
      const { description, amount, currency, id } = donationFee;

      setIsNewDonationScreen(true);
      setIsEditing(true);

      setDonationInEdit({
        description,
        amount,
        currency,
        id,
      });
    }
  };
  const renderDonations = (
    <DonationsTable
      isDonationFeatureEnabled={isDonationFeatureEnabled}
      handleEditDonation={handleEditDonation}
      handleDeleteModal={handleDeleteModal}
      deleteDonationPrice={deleteDonationPrice}
    />
  );
  const renderNewDonation = (
    <ContentContainer>
      <CreateDonationForm
        handleBackToDonations={handleNewDonation}
        onSubmit={handleSave}
        isEditing={isEditing}
        donationInEdit={donationInEdit}
        closePanel={closePanel}
      />
    </ContentContainer>
  );
  return (
    <>
      {isNewDonationScreen ? (
        renderNewDonation
      ) : (
        <>
          <Container>
            {!isDonationFeatureEnabled && (
              <StyledFlexContainer>
                <Typography variant="p">
                  The donation feature is currently not available for your account. Contact support at
                  support@inplayer.com to enable it.
                </Typography>
              </StyledFlexContainer>
            )}
            {isDevelopmentEnv && <AttentionMessage />}
            <Button
              tag="button_new_donation"
              disabled={!isDonationFeatureEnabled}
              type="button"
              icon={<FiPlus />}
              onClick={handleNewDonation(true)}
            >
              New donation
            </Button>
            <Wrapper>
              {isLoading ? (
                <LoaderContainer>
                  <Loader />
                </LoaderContainer>
              ) : (
                renderDonations
              )}
            </Wrapper>
            <Wrapper>
              <Switch
                tag="toggle_enable_custom_donations"
                disabled={!isDonationFeatureEnabled}
                checked={isEnabledCustomOption}
                id="custom-option"
                label="Enable custom donation"
                onChange={handleEnabledCustomOption}
              />
            </Wrapper>
          </Container>
        </>
      )}
    </>
  );
};

export default Donations;
